import React, { useState } from "react";
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";

const UploadTeamMembersModal = ({
  show,
  onClose,
  onUpload,
  onDownloadTemplate,
  loading,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");

  if (!show) return null;

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const validateFile = (file) => {
    setError("");
    if (!file) return false;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      setError("Only Excel files (.xlsx, .xls) are allowed.");
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("File size exceeds 10MB limit.");
      return false;
    }
    return true;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      } else {
        setSelectedFile(null);
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
    } else if (file) {
      setSelectedFile(null);
    }
  };

  const handleUploadClick = async () => {
    if (!selectedFile) return;
    await onUpload(selectedFile);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError("");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-900 w-full max-w-md rounded-xl shadow-2xl relative overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-brand to-brand-dark px-6 py-5 relative overflow-hidden">
          <div className="flex items-start justify-between relative z-10">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-1">
                Upload Team Members
              </h2>
              <p className="text-slate-300 text-xs leading-relaxed">
                Upload an Excel file to add team members in bulk
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-slate-300 hover:text-white transition-colors duration-200 p-1.5 rounded-lg ml-3 flex-shrink-0"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 dark:bg-gray-900">
          <div className="space-y-4">
            {/* Download Template Button */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-xs text-blue-700 dark:text-blue-300 font-medium mb-2">
                Download the sample template to see the required format:
              </p>
              <button
                type="button"
                onClick={onDownloadTemplate}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
                Example Excel Format
              </button>
            </div>

            {/* File Upload Area */}
            <div>
              <label className="block text-sm font-bold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">
                Excel File
              </label>
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-xl px-4 py-8 transition-all duration-300 cursor-pointer group ${
                  dragActive
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                }`}
              >
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="text-center">
                  {selectedFile ? (
                    <div className="space-y-2">
                      <div className="flex justify-center">
                        <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-full">
                          <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                      </div>
                      <div>
                        <p className="text-green-700 dark:text-green-400 font-semibold text-xs">
                          {selectedFile.name}
                        </p>
                        <p className="text-green-600 dark:text-green-500 text-xs mt-0.5">
                          {formatFileSize(selectedFile.size)}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-center">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full group-hover:scale-110 transition-transform duration-200">
                          <ArrowUpTrayIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-900 dark:text-white font-semibold text-xs">
                          Drag and drop your Excel file
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 text-xs mt-0.5">
                          or click to browse
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-xs text-red-700 dark:text-red-400 font-medium">
                    {error}
                  </p>
                </div>
              )}

              <div className="mt-3 p-3 bg-slate-50 dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700">
                <p className="text-xs text-slate-700 dark:text-gray-300 font-medium mb-2">
                  Accepted formats:
                </p>
                <div className="flex flex-wrap gap-1">
                  {["XLSX", "XLS"].map((format) => (
                    <span
                      key={format}
                      className="inline-block px-2 py-1 bg-white dark:bg-gray-700 border border-slate-300 dark:border-gray-600 rounded-full text-xs text-slate-600 dark:text-gray-300 font-medium"
                    >
                      {format}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-slate-600 dark:text-gray-400 mt-2">
                  Maximum file size: 10MB
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleUploadClick}
                disabled={!selectedFile || loading || !!error}
                className={`w-full font-semibold py-2.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 text-sm ${
                  selectedFile && !loading && !error
                    ? "bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white shadow-lg hover:shadow-xl cursor-pointer"
                    : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <ArrowUpTrayIcon className="w-4 h-4" />
                    <span>Upload Members</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="w-full font-semibold py-2.5 px-4 rounded-lg bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-gray-300 hover:bg-slate-200 dark:hover:bg-gray-600 transition-all duration-200 border border-slate-300 dark:border-gray-600 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadTeamMembersModal;
